import apiClient from '../utils/apiClient';
import { handleApiError } from '../utils/errorHandler';

interface AccountType {
  id: string | number;
  name: string;
  description?: string;
  isActive?: boolean;
  [key: string]: any;
}

interface AccountTypeListResponse {
  data: AccountType[];
  total: number;
  page: number;
  limit: number;
}

interface Filters {
  [key: string]: string | number | boolean | undefined;
}

class AccountTypeService {
  getAccountTypes(page = 1, limit = 10, search = "", filters: Filters = {}): Promise<AccountTypeListResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('page', page.toString());
    queryParams.append('limit', limit.toString());
    
    if (search) {
      queryParams.append('search', search);
    }
    
    // Apply any additional filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });
    
    return apiClient.get(`/admin/account-types?${queryParams.toString()}`)
      .then(response => {
        return response.data;
      })
      .catch(error => {
        handleApiError(error);
        throw error;
      });
  }

  getAccountType(id: string | number): Promise<AccountType> {
    try {
      return apiClient.get(`/admin/account-types/${id}`)
        .then(response => {
          return response.data;
        })
        .catch(error => {
          handleApiError(error);
          throw error;
        });
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  }

  createAccountType(data: Partial<AccountType>): Promise<AccountType> {
    return apiClient.post('/admin/account-types', data)
      .then(response => {
        return response.data;
      })
      .catch(error => {
        handleApiError(error);
        throw error;
      });
  }

  updateAccountType(id: string | number, data: Partial<AccountType>): Promise<AccountType> {
    // Clean field data
    if (data.fields && Array.isArray(data.fields)) {
      data.fields = data.fields.map(field => {
        // Create a clean copy of the field
        const cleanField = { ...field };
        
        // If field has options, clean them too
        if (cleanField.options) {
          // Create a clean copy of options
          const cleanOptions = { ...cleanField.options };
          
          // For non-price fields, don't include empty priceMatrix
          if (field.type !== 'price' && cleanOptions.priceMatrix && 
              Array.isArray(cleanOptions.priceMatrix) && cleanOptions.priceMatrix.length === 0) {
            // Remove empty priceMatrix to prevent validation errors
            delete cleanOptions.priceMatrix;
          }
          
          // Put clean options back in field
          cleanField.options = cleanOptions;
        }
        
        return cleanField;
      });
    }
    
    return apiClient.put(`/admin/account-types/${id}`, data)
      .then(response => {
        return response.data;
      })
      .catch(error => {
        handleApiError(error);
        throw error;
      });
  }

  deleteAccountType(id: string | number): Promise<any> {
    return apiClient.delete(`/admin/account-types/${id}`)
      .then(response => {
        return response.data;
      })
      .catch(error => {
        handleApiError(error);
        throw error;
      });
  }

  restoreAccountType(id: string | number): Promise<AccountType> {
    return apiClient.patch(`/admin/account-types/${id}/restore`)
      .then(response => {
        return response.data;
      })
      .catch(error => {
        handleApiError(error);
        throw error;
      });
  }

  toggleAccountTypeStatus(id: string | number): Promise<AccountType> {
    return apiClient.patch(`/admin/account-types/${id}/toggle-status`)
      .then(response => {
        return response.data;
      })
      .catch(error => {
        handleApiError(error);
        throw error;
      });
  }
}

export default new AccountTypeService(); 